import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MapPin, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/store/authStore';
import {
  getDailySetup,
  saveDailySetup,
  updateDSRShift,
  type ShiftType,
} from '@/services/db/dsrEntry';
import { getCurrentLocationWeather, type WeatherData } from '@/services/api/weather';

// ─── Constants ──────────────────────────────────────────────────────────────

const WEATHER_CONDITIONS = [
  'Clear Sky', 'Partly Cloudy', 'Overcast', 'Rain', 'Heavy Rain',
  'Foggy', 'Storm', 'Drizzle', 'Snow',
] as const;

const RIG_ACTIVITY_CODES: { value: string; label: string }[] = [
  { value: 'NORMAL_OPS',  label: 'Normal Production' },
  { value: 'START_UP',    label: 'Start-Up' },
  { value: 'SHUTDOWN',    label: 'Shutdown' },
  { value: 'TURNAROUND',  label: 'Turnaround' },
  { value: 'TESTING',     label: 'Testing' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'STANDBY',     label: 'Standby' },
  { value: 'OTHER',       label: 'Other' },
];

const SEA_STATE_LABELS: Record<number, string> = {
  0: 'Calm', 1: 'Light Air', 2: 'Light Breeze', 3: 'Gentle Breeze',
  4: 'Moderate Breeze', 5: 'Fresh Breeze', 6: 'Strong Breeze',
  7: 'Near Gale', 8: 'Gale', 9: 'Strong Gale',
  10: 'Storm', 11: 'Violent Storm', 12: 'Hurricane',
};

const SHIFTS: { value: ShiftType; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
  { value: 'full', label: 'Full' },
];

// ─── Schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  shift_type: z.enum(['day', 'night', 'full']),
  weather_condition: z.string().optional(),
  wind_speed_knots: z.string().optional(),
  visibility_nm: z.string().optional(),
  sea_state_beaufort: z.number().int().min(0).max(12).nullable(),
  temperature_celsius: z.string().optional(),
  site_conditions_notes: z.string().optional(),
  rig_activity_codes: z.array(z.string()).optional(),
  wind_direction: z.string().optional(),
  precipitation: z.string().optional(),
  weather_source: z.enum(['auto', 'manual']).optional(),
  weather_fetched_at: z.string().optional(),
  gps_latitude: z.number().nullable().optional(),
  gps_longitude: z.number().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildSavePayload(
  values: FormValues,
  dsrId: string,
  tenantId: string,
) {
  return {
    dsrReportId: dsrId,
    tenantId,
    weatherCondition: values.weather_condition || null,
    windSpeedKnots: values.wind_speed_knots ? parseInt(values.wind_speed_knots, 10) : null,
    visibilityNm: values.visibility_nm ? parseFloat(values.visibility_nm) : null,
    seaStateBeaufort: values.sea_state_beaufort,
    temperatureCelsius: values.temperature_celsius ? parseFloat(values.temperature_celsius) : null,
    siteConditionsNotes: values.site_conditions_notes || null,
    rigActivityCodes: values.rig_activity_codes?.length ? values.rig_activity_codes : null,
    windDirection: values.wind_direction || null,
    precipitation: values.precipitation || null,
    weatherFetchedAt: values.weather_fetched_at || null,
    weatherSource: values.weather_source || null,
    gpsLatitude: values.gps_latitude ?? null,
    gpsLongitude: values.gps_longitude ?? null,
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
  dsrId: string;
  initialShiftType: ShiftType;
  isReadOnly?: boolean;
}

export default function DailySetupForm({ dsrId, initialShiftType, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherSuccess, setWeatherSuccess] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const weatherFetched = useRef(false);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['entry_daily_setup', dsrId],
    queryFn: () => getDailySetup(dsrId),
    staleTime: 30 * 1000,
  });

  const { control, reset, getValues, setValue, watch } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      shift_type: initialShiftType,
      weather_condition: '',
      wind_speed_knots: '',
      visibility_nm: '',
      sea_state_beaufort: null,
      temperature_celsius: '',
      site_conditions_notes: '',
      rig_activity_codes: [],
      wind_direction: '',
      precipitation: '',
      weather_source: undefined,
      weather_fetched_at: undefined,
      gps_latitude: null,
      gps_longitude: null,
    },
  });

  // Hydrate form from server.
  useEffect(() => {
    if (existing === undefined) return;
    reset({
      shift_type: initialShiftType,
      weather_condition: existing?.weather_condition ?? '',
      wind_speed_knots:
        existing?.wind_speed_knots != null ? String(existing.wind_speed_knots) : '',
      visibility_nm:
        existing?.visibility_nm != null ? String(existing.visibility_nm) : '',
      sea_state_beaufort: existing?.sea_state_beaufort ?? null,
      temperature_celsius:
        existing?.temperature_celsius != null ? String(existing.temperature_celsius) : '',
      site_conditions_notes: existing?.site_conditions_notes ?? '',
      rig_activity_codes: existing?.rig_activity_codes ?? [],
      wind_direction: existing?.wind_direction ?? '',
      precipitation: existing?.precipitation ?? '',
      weather_source: existing?.weather_source ?? undefined,
      weather_fetched_at: existing?.weather_fetched_at ?? undefined,
      gps_latitude: existing?.gps_latitude ?? null,
      gps_longitude: existing?.gps_longitude ?? null,
    });
  }, [existing, reset, initialShiftType]);

  // Auto-fetch weather for new DSR (no existing daily setup row).
  useEffect(() => {
    if (isLoading || weatherFetched.current) return;
    if (existing != null) return;
    weatherFetched.current = true;
    setWeatherLoading(true);
    getCurrentLocationWeather()
      .then((data: WeatherData | null) => {
        if (data) {
          setValue('weather_condition', data.conditions);
          setValue('temperature_celsius', String(data.temperatureCelsius));
          setValue('wind_speed_knots', String(Math.round(data.windSpeedKnots)));
          setValue('wind_direction', data.windDirection);
          setValue('visibility_nm', String(data.visibilityNm));
          setValue('precipitation', data.precipitation);
          setValue('weather_source', 'auto');
          setValue('weather_fetched_at', data.fetchedAt);
          setValue('gps_latitude', data.latitude);
          setValue('gps_longitude', data.longitude);
          setWeatherSuccess(true);
          setTimeout(() => setWeatherSuccess(false), 5000);
          // Persist auto-fetched values immediately.
          void persist();
        } else {
          setValue('weather_source', 'manual');
        }
      })
      .finally(() => setWeatherLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, existing]);

  async function persist() {
    if (isReadOnly || !tenantId) return;
    setSaving(true);
    try {
      const values = getValues();
      await Promise.all([
        saveDailySetup(buildSavePayload(values, dsrId, tenantId)),
        updateDSRShift(dsrId, values.shift_type),
      ]);
      setSavedAt(new Date());
      queryClient.invalidateQueries({ queryKey: ['entry_daily_setup', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_dsr_header', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch {
      // Silent: auto-save failures should not interrupt typing. The next
      // save attempt or explicit submit will surface a real error.
    } finally {
      setSaving(false);
    }
  }

  // 30-second auto-save interval.
  useEffect(() => {
    if (isReadOnly) return;
    const id = window.setInterval(() => {
      void persist();
    }, 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, dsrId, tenantId]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  const onBlurSave = () => void persist();
  const weatherSource = watch('weather_source');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Daily Setup</h2>
          <p className="text-xs text-muted-foreground">
            Weather, sea state, and rig activity for the reporting period.
          </p>
        </div>
        <SaveIndicator saving={saving} savedAt={savedAt} />
      </div>

      {/* Shift */}
      <Card>
        <CardContent className="space-y-3 pt-6">
          <FieldLabel required>Shift</FieldLabel>
          <Controller
            control={control}
            name="shift_type"
            render={({ field }) => (
              <ChipGroup
                options={SHIFTS}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v);
                  void persist();
                }}
                disabled={isReadOnly}
              />
            )}
          />
        </CardContent>
      </Card>

      {/* Weather */}
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-mozaic-navy">
              Weather
            </h3>
            {weatherLoading && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Fetching weather…
              </span>
            )}
            {weatherSource === 'auto' && !weatherLoading && (
              <span className="flex items-center gap-1 rounded-full bg-mozaic-teal/15 px-2 py-0.5 text-[10px] font-semibold text-mozaic-teal">
                <MapPin className="h-3 w-3" /> Auto
              </span>
            )}
          </div>
          {weatherSuccess && (
            <p className="text-xs text-mozaic-teal">
              Weather fetched automatically — edit if needed.
            </p>
          )}

          {/* Conditions chips */}
          <div className="space-y-2">
            <FieldLabel>Weather Condition</FieldLabel>
            <Controller
              control={control}
              name="weather_condition"
              render={({ field }) => (
                <ChipGroup
                  options={WEATHER_CONDITIONS.map((c) => ({ value: c, label: c }))}
                  value={field.value ?? ''}
                  onChange={(v) => {
                    field.onChange(v === field.value ? '' : v);
                    void persist();
                  }}
                  disabled={isReadOnly}
                  wrap
                />
              )}
            />
          </div>

          {/* Temp + wind speed */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberField
              control={control}
              name="temperature_celsius"
              label="Temperature (°F)"
              placeholder="e.g. 22.5"
              onBlurSave={onBlurSave}
              disabled={isReadOnly}
              decimal
            />
            <NumberField
              control={control}
              name="wind_speed_knots"
              label="Wind Speed (knots)"
              placeholder="e.g. 15"
              onBlurSave={onBlurSave}
              disabled={isReadOnly}
            />
          </div>

          {/* Wind direction + visibility */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              control={control}
              name="wind_direction"
              label="Wind Direction"
              placeholder="e.g. NNW"
              onBlurSave={onBlurSave}
              disabled={isReadOnly}
            />
            <NumberField
              control={control}
              name="visibility_nm"
              label="Visibility (nm)"
              placeholder="e.g. 5.0"
              onBlurSave={onBlurSave}
              disabled={isReadOnly}
              decimal
            />
          </div>

          <TextField
            control={control}
            name="precipitation"
            label="Precipitation"
            placeholder="e.g. None, Light Rain"
            onBlurSave={onBlurSave}
            disabled={isReadOnly}
          />

          {/* Sea state */}
          <div className="space-y-2">
            <FieldLabel>Sea State (Beaufort)</FieldLabel>
            <Controller
              control={control}
              name="sea_state_beaufort"
              render={({ field }) => (
                <div className="flex flex-wrap gap-1.5">
                  {Array.from({ length: 13 }, (_, i) => {
                    const selected = field.value === i;
                    return (
                      <button
                        type="button"
                        key={i}
                        disabled={isReadOnly}
                        onClick={() => {
                          field.onChange(selected ? null : i);
                          void persist();
                        }}
                        className={`min-w-[52px] rounded-lg border px-3 py-1.5 text-center text-xs transition-colors ${
                          selected
                            ? 'border-mozaic-teal bg-mozaic-teal text-white'
                            : 'border-input bg-background hover:bg-muted'
                        }`}
                      >
                        <div className="text-sm font-bold">{i}</div>
                        <div className="text-[10px] leading-tight opacity-80">
                          {SEA_STATE_LABELS[i]}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Site conditions notes */}
      <Card>
        <CardContent className="space-y-2 pt-6">
          <FieldLabel>Site Conditions Notes</FieldLabel>
          <Controller
            control={control}
            name="site_conditions_notes"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                onBlur={() => {
                  field.onBlur();
                  onBlurSave();
                }}
                disabled={isReadOnly}
                rows={4}
                placeholder="Any notable site or environmental conditions…"
              />
            )}
          />
        </CardContent>
      </Card>

      {/* Rig activity codes */}
      <Card>
        <CardContent className="space-y-2 pt-6">
          <FieldLabel>Rig Activity</FieldLabel>
          <Controller
            control={control}
            name="rig_activity_codes"
            render={({ field }) => {
              const value = field.value ?? [];
              return (
                <div className="flex flex-wrap gap-2">
                  {RIG_ACTIVITY_CODES.map(({ value: code, label }) => {
                    const selected = value.includes(code);
                    return (
                      <button
                        type="button"
                        key={code}
                        disabled={isReadOnly}
                        onClick={() => {
                          const next = selected
                            ? value.filter((c) => c !== code)
                            : [...value, code];
                          field.onChange(next);
                          void persist();
                        }}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          selected
                            ? 'border-mozaic-teal bg-mozaic-teal text-white'
                            : 'border-input bg-background hover:bg-muted'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              );
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
      {required && <span className="ml-1 text-mozaic-red">*</span>}
    </Label>
  );
}

function ChipGroup<T extends string>({
  options,
  value,
  onChange,
  disabled,
  wrap,
}: {
  options: readonly { value: T; label: string }[];
  value: T | '';
  onChange: (v: T) => void;
  disabled?: boolean;
  wrap?: boolean;
}) {
  return (
    <div className={`flex gap-2 ${wrap ? 'flex-wrap' : ''}`}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              selected
                ? 'border-mozaic-teal bg-mozaic-teal text-white'
                : 'border-input bg-background hover:bg-muted'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function TextField({
  control,
  name,
  label,
  placeholder,
  onBlurSave,
  disabled,
}: {
  control: any;
  name: string;
  label: string;
  placeholder?: string;
  onBlurSave: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <FieldLabel>{label}</FieldLabel>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ''}
            onBlur={() => {
              field.onBlur();
              onBlurSave();
            }}
            placeholder={placeholder}
            disabled={disabled}
          />
        )}
      />
    </div>
  );
}

function NumberField({
  control,
  name,
  label,
  placeholder,
  onBlurSave,
  disabled,
  decimal,
}: {
  control: any;
  name: string;
  label: string;
  placeholder?: string;
  onBlurSave: () => void;
  disabled?: boolean;
  decimal?: boolean;
}) {
  return (
    <div className="space-y-2">
      <FieldLabel>{label}</FieldLabel>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ''}
            type="number"
            inputMode={decimal ? 'decimal' : 'numeric'}
            step={decimal ? '0.1' : '1'}
            onBlur={() => {
              field.onBlur();
              onBlurSave();
            }}
            placeholder={placeholder}
            disabled={disabled}
          />
        )}
      />
    </div>
  );
}

function SaveIndicator({ saving, savedAt }: { saving: boolean; savedAt: Date | null }) {
  if (saving) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className="flex items-center gap-1 text-xs text-mozaic-green">
        <CheckCircle2 className="h-3 w-3" /> Saved
      </span>
    );
  }
  return null;
}
